import {Injectable} from '@angular/core'
import {Auth, CognitoUser} from '@aws-amplify/auth'
import {ConfigService} from '../config/config.service'
import {AppConfig} from '../config/config.types'
import {CognitoUserPool} from 'amazon-cognito-identity-js'
import {LocalStorageService} from '../storage/local-storage.service'
import {AdminRole, AuthenticatedUser, AuthStatusService, ProRole} from './auth-status.service'
import {ErrorMonitor} from '../error/error-monitoring.types'
import {getSeoUrl} from '../seo/util'
import {Logger} from '../logging/logger.types'
import {GraphqlLinkController} from '../graphql/graphql-link.controller'
import {AuthenticationError, AuthService} from './auth.service'

const COGNITO_USER_STORAGE_KEY = 'COGNITO_USER'

/**
 * This service contains a lot of third party code (aws amplify, cognito, etc) and should be always lazily loaded
 * THIS IS NOT A SINGLETON ! Due to how angular injectors work, as this is lazy loaded, it will be created in each module
 * therefore THIS CANNOT HOLD STATE !
 */
@Injectable()
export class AuthBrowserService extends AuthService {
  constructor(
    private readonly configService: ConfigService,
    private readonly logger: Logger,
    private readonly localStorageService: LocalStorageService,
    private readonly authStatusService: AuthStatusService,
    private readonly errorMonitor: ErrorMonitor,
    private readonly graphqlLinkController: GraphqlLinkController,
  ) {
    super()
    this.configure(this.configService.config)
    this.checkAuthStatus()
  }

  private configure(config: AppConfig): void {
    Auth.configure({
      region: config.cognito.region,
      userPoolId: config.cognito.poolId,
      userPoolWebClientId: config.cognito.appId,
      oauth: config.cognito.oauth
        ? {
            domain: config.cognito.oauth.domain,
            scope: ['openid', 'email', 'phone', 'profile'],
            redirectSignIn: getSeoUrl(config, '/') + `?signedIn=true`,
            redirectSignOut: getSeoUrl(config, '/') + `?signedOut=true`,
            responseType: 'code',
          }
        : undefined,
    })
  }

  async signIn(email: string): Promise<void> {
    try {
      const user = await Auth.signIn(email)

      // the session needs to be used when answering the custom auth challenge, persist it now
      this.persistCognitoUserSession({
        username: user.username,
        session: user.Session,
      })
    } catch (err) {
      if (err?.code === 'UserNotFoundException') {
        throw new AuthenticationError('USER_NOT_FOUND', err.message)
      } else if (err?.code === 'NotAuthorizedException') {
        throw new AuthenticationError('USER_DISABLED', err.message)
      } else {
        throw err
      }
    }
  }

  async completeSignInChallenge(email: string, answer: string): Promise<void> {
    // the following two lines is a workaround to support magic link authentication
    // the problem is that we need a cognitoUser instance when using `Auth.sendCustomChallengeAnswer`
    // but the user comes from a freshly created page as he clicked on the link in his email box
    // therefore we store the session in local storage, and attempt to rehydrate it now
    const userSession = this.readCognitoUserSession()
    const cognitoUser = this.hydrateCognitoUser(userSession)

    // send the answer to the User Pool
    const res = await Auth.sendCustomChallengeAnswer(cognitoUser, answer)

    if (res?.username && res?.Session) {
      // if auth failed, we got a new session which must be used for next attempt
      this.persistCognitoUserSession({username: res.username, session: res.Session})
    }

    try {
      // if we get here, the answer was sent successfully,
      // but it might have been wrong, so we should test if the user is authenticated now

      // this call is important (even if repeated in checkAuthStatus) because it will throw an error
      // and let us know that the user is not yet authenticated
      await Auth.currentSession()

      // didn't throw, we can continue
      await this.checkAuthStatus()
    } catch (err) {
      this.logger.log('Failed to complete sign in', err)
      throw new AuthenticationError('CHALLENGE_REJECTED', 'Failed to complete sign-in')
    }

    // cleanup
    this.localStorageService.removeItem(COGNITO_USER_STORAGE_KEY)
  }

  async signOut(): Promise<void> {
    if (this.authStatusService.isSignedIn()) {
      // apollo cache is sensitive and will throw "Invariant Violation: 21" when clearing the store while a query is in flight
      // this reduces the chance of this happening, especially when the user is not signed in and page is loading (content queries are in flight)
      // a better approach would be to separate the content cache from the user cache, so that we make sure to only clear the user cache
      await this.graphqlLinkController.removeAuth()
    }
    await Auth.signOut()
    await this.checkAuthStatus()
    this.errorMonitor.setUser(null)
  }

  private async checkAuthStatus() {
    try {
      const user = await Auth.currentAuthenticatedUser()
      if (user) {
        const session = await Auth.currentSession()
        const id = session.getIdToken().payload
        let userId = id['custom:userId']
        if (!userId) {
          // try to fallback to user attributes in case user ID isn't yet in the ID token
          userId = user.attributes ? user.attributes['custom:userId'] : undefined
        }
        const proId = id['custom:proId']
        const agentId = id['custom:agentId']
        const clientId = id['custom:clientId']
        const adminRoleBrn = id['bgo:admin_role']
        const adminRole = this.parseRole<AdminRole>(adminRoleBrn)
        const proRoleBrn = id['bgo:pro_role']
        const proRole = this.parseRole<ProRole>(proRoleBrn)

        const authenticatedUser: AuthenticatedUser = {
          userId,
          cognitoUsername: id.sub,
          email: id.email,
          phoneNumber: id.phone_number,
          firstName: id.given_name,
          lastName: id.family_name,
          locale: id.locale,
          isGlobalAdministrator: adminRole === 'global_admin',
          ...(proId && {pro: {id: proId, role: proRole}}),
          ...(agentId && {agent: {id: agentId}}),
          ...(clientId && {client: {id: clientId}}),
        }

        this.addAuthenticationToHttp()
        this.authStatusService.sendAuthStatusUpdate({
          isLoading: false,
          authenticatedUser,
        })
        this.errorMonitor.setUser({
          id: authenticatedUser.userId,
          cognitoUsername: authenticatedUser.cognitoUsername,
          email: authenticatedUser.email,
          clientId: authenticatedUser.client?.id,
          proId: authenticatedUser.pro?.id,
          proRole: authenticatedUser.pro?.role,
          agentId: authenticatedUser.agent?.id,
        })
        this.logger.debug('Auth status change, user is authenticated', {authenticatedUser})
      }
    } catch (err) {
      this.logger.debug('Caught err when checking user auth status', err)
      this.sendNoAuthStatus()
      this.logger.debug('Auth status change, user not defined')
    }
  }

  private sendNoAuthStatus() {
    this.authStatusService.sendAuthStatusUpdate({
      isLoading: false,
      authenticatedUser: undefined,
    })
  }

  /**
   * Update http client to send the auth token in the headers with every request
   */
  addAuthenticationToHttp() {
    const getJwtToken = () => this.getCurrentIdToken()
    this.graphqlLinkController.addAuth(getJwtToken)
  }

  private async getCurrentIdToken(): Promise<string | undefined> {
    const maxRetries = 5
    const startDelay = 500

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        this.logger.debug(`Attempt ${attempt + 1}: Getting current Id token`)

        // according to doc, Auth.currentSession will refresh the tokens automatically
        const session = await Auth.currentSession()
        return session.getIdToken().getJwtToken()
      } catch (err) {
        this.logger.debug(`Caught error on attempt ${attempt + 1}`, err)

        if (attempt === maxRetries - 1) {
          this.logger.error('Max retries reached. Unable to get Id token.')
          return undefined
        }

        // Exponential backoff
        const delay = Math.pow(2, attempt) * startDelay
        this.logger.debug(`Retrying in ${delay}ms...`)

        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }

    return undefined
  }

  private persistCognitoUserSession(userSession: RawCognitoUserSession) {
    this.localStorageService.setObject(COGNITO_USER_STORAGE_KEY, {
      username: userSession.username,
      session: userSession.session,
    })
  }

  private readCognitoUserSession(): RawCognitoUserSession {
    const cachedUser = this.localStorageService.getObject<RawCognitoUserSession>(COGNITO_USER_STORAGE_KEY)
    if (!cachedUser) {
      throw new AuthenticationError('WRONG_DEVICE', 'You signed-in in another device/browser, please start over')
    }
    return cachedUser
  }

  private hydrateCognitoUser(userSession: RawCognitoUserSession): CognitoUser {
    const userPool = new CognitoUserPool({
      UserPoolId: this.configService.config.cognito.poolId,
      ClientId: this.configService.config.cognito.appId,
    })
    const user = new CognitoUser({
      Username: userSession.username,
      Pool: userPool,
    })

    // set the session that is needed for the custom auth flow
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(user as any).Session = userSession.session
    user.setAuthenticationFlowType('CUSTOM_AUTH')

    return user
  }
}

export interface RawCognitoUserSession {
  username: string
  session: string
}
